/**
 * Reconciliation (P3-BR4, P3-TR5).
 *
 * The screen is built around one number: the difference between what the books
 * say and what is actually there. §11.1 asks for a large green or red
 * indicator, and the reason is that this is the moment a partner finds out
 * whether the farm's records are true — a figure buried in a form would be
 * skimmed past.
 *
 * The book balance is shown but never editable. It comes from the server, and
 * a screen that let a partner adjust it would turn a check into a formality.
 */

import { useMemo, useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorMessageKey } from '../../lib/apiError.js';
import { formatMoney } from '../../lib/currency.js';
import { todayInDhaka } from '../../lib/date.js';
import { useAccounts } from '../finance/api.js';
import { useReconcile, useReconcilePreview } from './api.js';

/** The partner's figure minus the book's, as a string (MONEY-1). */
export function differenceOf(bookBalance: string, actual: string): string {
  if (actual.trim() === '') return '0.00';

  // Paisa arithmetic on integers: 0.1 + 0.2 as floats is 0.30000000000000004.
  const paisa = Math.round(Number(actual) * 100) - Math.round(Number(bookBalance) * 100);
  return (paisa / 100).toFixed(2);
}

export function ReconciliationScreen(): JSX.Element {
  const { t } = useTranslation();

  const { data: accounts = [] } = useAccounts();
  const [accountId, setAccountId] = useState<number | null>(null);
  const [asOfDate, setAsOfDate] = useState(todayInDhaka());
  const [actual, setActual] = useState('');
  const [note, setNote] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<number | null>(null);

  const { data: preview } = useReconcilePreview(accountId, asOfDate);
  const reconcile = useReconcile();

  const bookBalance = preview?.bookBalance ?? '0.00';
  const difference = useMemo(() => differenceOf(bookBalance, actual), [bookBalance, actual]);

  const isClean = difference === '0.00';
  const needsNote = !isClean && note.trim().length < 3;
  const canSubmit = accountId !== null && actual.trim() !== '' && !needsNote && !reconcile.isPending;

  async function handleSubmit(): Promise<void> {
    if (accountId === null) return;
    setErrorKey(null);

    try {
      const result = await reconcile.mutateAsync({
        accountId,
        asOfDate,
        actualBalance: actual,
        ...(note.trim() !== '' ? { note: note.trim() } : {}),
      });

      setSavedId(result.id);
      setActual('');
      setNote('');
    } catch (error) {
      setErrorKey(errorMessageKey(error));
    }
  }

  return (
    <form
      className="page-container"
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
      noValidate
    >
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('reconcile.title')}</h1>

      <fieldset className="mb-4">
        <legend className="mb-2 text-sm font-medium text-gray-700">{t('reconcile.account')}</legend>
        <div className="flex flex-wrap gap-2">
          {accounts.map((account) => (
            <button
              key={account.id}
              type="button"
              onClick={() => setAccountId(account.id)}
              aria-pressed={accountId === account.id}
              className={`min-h-touch rounded-full px-4 py-2 text-sm ${
                accountId === account.id ? 'bg-green-700 font-medium text-white' : 'bg-white text-gray-700 shadow-sm'
              }`}
            >
              {account.name}
            </button>
          ))}
        </div>
      </fieldset>

      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="asOf">
        {t('reconcile.asOf')}
      </label>
      <input
        id="asOf"
        type="date"
        value={asOfDate}
        max={todayInDhaka()}
        onChange={(event) => setAsOfDate(event.target.value)}
        className="mb-4 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />

      {accountId !== null && (
        <>
          <div className="mb-3 rounded-xl bg-white px-4 py-3 shadow-sm">
            <p className="text-xs text-gray-500">{t('reconcile.bookSays')}</p>
            {/* Shown, never editable: a partner who could adjust this would
                turn the check into a formality. */}
            <p className="mt-0.5 text-2xl font-semibold tabular-nums text-gray-900">{formatMoney(bookBalance)}</p>
            {preview?.lastReconciled !== null && preview !== undefined && (
              <p className="mt-1 text-xs text-gray-500">
                {t('reconcile.lastChecked', { date: preview.lastReconciled ?? '' })}
              </p>
            )}
          </div>

          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="actual">
            {t('reconcile.youCounted')}
          </label>
          <input
            id="actual"
            inputMode="decimal"
            placeholder="0.00"
            value={actual}
            onChange={(event) => setActual(event.target.value)}
            className="mb-4 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3 text-right text-xl tabular-nums"
          />

          {/* §11.1 — the big green or red number. This is the moment a partner
              learns whether the records are true. */}
          {actual.trim() !== '' && (
            <div
              role="status"
              className={`mb-4 rounded-xl px-4 py-4 text-center ${
                isClean ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}
            >
              <p className="text-sm">{t(isClean ? 'reconcile.matches' : 'reconcile.differs')}</p>
              <p className="mt-1 text-3xl font-semibold tabular-nums">{formatMoney(difference)}</p>
            </div>
          )}

          {!isClean && actual.trim() !== '' && (
            <>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="note">
                {t('reconcile.explain')}
              </label>
              <input
                id="note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                className="mb-4 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
              />
            </>
          )}
        </>
      )}

      {errorKey !== null && (
        <p role="alert" className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          {t(errorKey)}
        </p>
      )}

      {savedId !== null && errorKey === null && (
        <p className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">{t('reconcile.saved')}</p>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className="min-h-[52px] w-full rounded-xl bg-green-700 text-lg font-medium text-white disabled:opacity-50"
      >
        {t(reconcile.isPending ? 'reconcile.saving' : 'reconcile.submit')}
      </button>
    </form>
  );
}
